import React, { Dispatch, SetStateAction } from "react";

import { Button, Select, SelectChangeEvent } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { useAuth } from "../../contexts/AuthContext";
import { ATPUser } from "../../services/api";
import { UserRole } from "../../services/api/AuthUser";
import { CentredContentCell, DefaultHeaderCell } from "../../theme";
import ConfirmationDialog from "../shared/DialogConfirmation";
import AddUserMenu from "./AddUserMenu";

const UsersTable: React.FC<{
  users: ATPUser[];
  setUsers: Dispatch<SetStateAction<ATPUser[]>>;
  updateRole: (user: ATPUser) => void;
  deleteUser: (user: ATPUser) => void;
}> = ({ users, setUsers, updateRole, deleteUser }) => {
  const { currentUser } = useAuth();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<ATPUser>();
  const handleRoleChange = (event: SelectChangeEvent, id: string) => {
    const newUsers = users.map((user) => {
      if (user.id === id) user.role = event.target.value;
      return user;
    });
    setUsers(newUsers);
  };

  const onUpdateClicked = (user: ATPUser, role: string) => {
    user.role = role;
    updateRole(user);
  };

  const onDeleteClicked = (user: ATPUser) => {
    setUserToDelete(user);
    setDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDialogOpen(false);
    deleteUser(userToDelete!);
  };

  const deleteButton = (row: ATPUser) => {
    return (
      <CentredContentCell>
        <Button
          color="error"
          disabled={currentUser.role !== UserRole.ADMIN}
          onClick={() => onDeleteClicked(row)}
          variant="outlined"
          data-testid={"delete-user-button"}
          sx={{ minWidth: 85 }}
        >
          Delete
        </Button>
      </CentredContentCell>
    );
  };

  const updateButton = (row: ATPUser) => {
    return (
      <CentredContentCell>
        <Button
          disabled={currentUser.role !== UserRole.ADMIN}
          onClick={() => onUpdateClicked(row, row.role)}
          variant="outlined"
          data-testid={"update-role-button"}
          sx={{ minWidth: 90 }}
        >
          Update
        </Button>
      </CentredContentCell>
    );
  };

  return (
    <>
      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete ${userToDelete?.username} ${userToDelete?.first_name} ${userToDelete?.last_name}  ?`}
      />
      <AddUserMenu></AddUserMenu>
      <TableContainer sx={{ marginTop: "20px" }}>
        <Table>
          <TableHead>
            <TableRow>
              <DefaultHeaderCell>User</DefaultHeaderCell>
              <DefaultHeaderCell>Name</DefaultHeaderCell>
              <DefaultHeaderCell>Role</DefaultHeaderCell>
              <DefaultHeaderCell>Action</DefaultHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((row) => (
              <TableRow key={row.id}>
                <CentredContentCell sx={{ minWidth: 120 }}>{row.username}</CentredContentCell>
                <CentredContentCell sx={{ minWidth: 120 }}>
                  {row.first_name} {row.last_name}
                </CentredContentCell>
                <CentredContentCell>
                  <Select
                    sx={{ minWidth: "120px" }}
                    size="small"
                    disabled={currentUser.role !== UserRole.ADMIN}
                    value={row.role}
                    onChange={(e) => handleRoleChange(e, row.id)}
                    inputProps={{ "data-testid": "role-selection" }}
                  >
                    <MenuItem value={"admin"}>admin</MenuItem>
                    <MenuItem value={"operator"}>operator</MenuItem>
                    <MenuItem value={"guest"}>guest</MenuItem>
                  </Select>
                </CentredContentCell>
                {row.hasChangedRole() ? updateButton(row) : deleteButton(row)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default UsersTable;
